import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AppShell, useShell } from '../components/AppShell';
import { useEntitlements } from '../lib/entitlements';
import { isValidUKMobile } from '../lib/phone';

const initials = (name) => name?.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
const fmtMoney = (n) => `£${Number(n || 0).toFixed(0)}`;
const fmtDate = (iso) => {
  const d = new Date(iso);
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
  return `${d.getDate()} ${m} ${d.getFullYear()}`;
};
const daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

const STATUS_PILL = {
  completed: { cls: 'green', label: 'Completed' },
  confirmed: { cls: 'prem', label: 'Upcoming' },
  no_show:   { cls: 'amber', label: 'No-show' },
  cancelled: { cls: 'gray',  label: 'Cancelled' },
};

const segmentFor = (visits, lastVisit) => {
  if (visits === 0) return null;
  if (visits === 1) return { cls: 'green', label: 'New' };
  const ds = daysSince(lastVisit);
  if (visits >= 5 && (ds ?? 0) <= 60) return { cls: 'prem', label: 'Regular' };
  if ((ds ?? 0) > 30) return { cls: 'amber', label: 'Lapsing' };
  return null;
};

const ClientDetailInner = () => {
  const { id } = useParams();
  const { profile } = useShell();
  const { features } = useEntitlements();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);

  // Inline-edit state for name / phone / email. `editing` gates the
  // input mode; `draft` holds the working copy so Cancel can throw it
  // away without touching `client` (which stays the read-only source
  // of truth until Save succeeds).
  const [editing, setEditing]   = useState(false);
  const [draft, setDraft]       = useState({ name: '', phone: '', email: '' });
  const [saving, setSaving]     = useState(false);
  const [editError, setEditError] = useState('');

  const startEdit = () => {
    setDraft({
      name:  client?.name  || '',
      phone: client?.phone || '',
      email: client?.email || '',
    });
    setEditError('');
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setEditError('');
  };
  const saveEdit = async () => {
    setEditError('');
    if (!draft.name.trim()) {
      setEditError('Name is required.');
      return;
    }
    // UK-mobile guard when a phone is present. Empty phone is allowed
    // (matches the AddClient modal).
    if (draft.phone && !isValidUKMobile(draft.phone)) {
      setEditError('Enter a UK mobile as 07xxx xxxxxx.');
      return;
    }
    setSaving(true);
    const patch = {
      name:  draft.name.trim(),
      phone: draft.phone.trim() || null,
      email: draft.email.trim() || null,
    };
    const { error } = await supabase
      .from('customers').update(patch).eq('id', id);
    setSaving(false);
    if (error) {
      setEditError(error.message || 'Could not save changes.');
      return;
    }
    setClient(prev => ({ ...(prev || {}), ...patch }));
    setEditing(false);
  };

  useEffect(() => {
    if (!profile?.shop_id || !id) return;
    (async () => {
      setLoading(true);
      const { data: c } = await supabase
        .from('customers').select('*')
        .eq('id', id).eq('shop_id', profile.shop_id).single();
      setClient(c);

      const { data: bk } = await supabase
        .from('bookings')
        .select('id,starts_at,status,deposit_paid,deposit_amount,service:services(name,price),barber:barbers(name)')
        .eq('shop_id', profile.shop_id)
        .eq('customer_id', id)
        .order('starts_at', { ascending: false });
      setBookings(bk || []);
      setLoading(false);
    })();
  }, [id, profile?.shop_id]);

  // Aggregate stats — only count PAST bookings for "visits" / "last visit"
  const now = Date.now();
  const completed = bookings.filter(b => b.status === 'completed');
  const pastBookings = bookings.filter(b =>
    new Date(b.starts_at).getTime() <= now &&
    b.status !== 'cancelled'
  );
  const visits = completed.length;
  const spend  = completed.reduce((s, b) => s + Number(b.service?.price || 0), 0);
  const lastVisit = pastBookings[0]?.starts_at || null;

  // Avg every (days between completed visits)
  let avgEvery = null;
  if (completed.length >= 2) {
    const sorted = [...completed].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
    const gaps = [];
    for (let i = 1; i < sorted.length; i++) {
      gaps.push((new Date(sorted[i].starts_at) - new Date(sorted[i-1].starts_at)) / 86400000);
    }
    avgEvery = Math.round(gaps.reduce((a,b) => a+b, 0) / gaps.length);
  }
  const lastVisitDays = daysSince(lastVisit);
  const seg = segmentFor(visits, lastVisit);

  return (
    <main className="main">
      <div className="topbar">
        <div className="bc">
          <Link to="/clients">Clients</Link>
          <span className="sep">/</span>
          <span className="cur">{client?.name || '—'}</span>
        </div>
        <div className="av" style={{ marginLeft: 'auto' }}>
          {(profile?.full_name || 'M').charAt(0).toUpperCase()}
        </div>
      </div>

      <div className="body">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>
        ) : !client ? (
          <div className="statebox">
            <h5>Client not found</h5>
            <p>It may have been removed.</p>
          </div>
        ) : (
          <>
            <div className="client-hero" data-testid="client-hero">
              <span className="circ">{initials(editing ? draft.name : client.name)}</span>
              {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
                  <input
                    className="inp"
                    value={draft.name}
                    onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
                    placeholder="Name"
                    data-testid="client-name-input"
                  />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <input
                      className="inp"
                      value={draft.phone}
                      onChange={(e) => setDraft(d => ({ ...d, phone: e.target.value }))}
                      placeholder="Phone (07xxx xxxxxx)"
                      inputMode="tel"
                      data-testid="client-phone-input"
                      style={{ flex: '1 1 200px' }}
                    />
                    <input
                      className="inp"
                      type="email"
                      value={draft.email}
                      onChange={(e) => setDraft(d => ({ ...d, email: e.target.value }))}
                      placeholder="Email"
                      data-testid="client-email-input"
                      style={{ flex: '1 1 200px' }}
                    />
                  </div>
                  {editError && (
                    <div className="phone-input-error" data-testid="client-edit-error">{editError}</div>
                  )}
                </div>
              ) : (
                <div>
                  <b className="name">{client.name}</b>
                  <span className="meta">
                    {client.phone || '—'}{client.email ? ` · ${client.email}` : ''}
                  </span>
                </div>
              )}
              {!editing && seg && <span className={`pill ${seg.cls}`} style={{ marginLeft: 6 }}>{seg.label}</span>}
              <div className="actions">
                {editing ? (
                  <>
                    <button
                      className="btn p"
                      onClick={saveEdit}
                      disabled={saving}
                      data-testid="client-save-btn"
                    >{saving ? 'Saving…' : 'Save'}</button>
                    <button
                      className="btn s"
                      onClick={cancelEdit}
                      disabled={saving}
                      data-testid="client-cancel-btn"
                    >Cancel</button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn s"
                      onClick={startEdit}
                      data-testid="client-edit-toggle"
                    >Edit</button>
                    <button
                      className="btn s"
                      onClick={() => { if (client.email) window.location.href = `mailto:${client.email}`; }}
                      disabled={!client.email}
                      data-testid="client-message-btn"
                    >Message</button>
                    <button
                      className="btn p"
                      onClick={() => navigate('/diary')}
                      data-testid="client-book-btn"
                    >+ Book in</button>
                  </>
                )}
              </div>
            </div>

            <div className="kpis" data-testid="client-kpis">
              <div className="kpi">
                <div className="t">Total visits</div>
                <div className="v">{visits}</div>
              </div>
              <div className="kpi">
                <div className="t">Lifetime spend</div>
                <div className="v">{fmtMoney(spend)}</div>
              </div>
              <div className="kpi">
                <div className="t">Avg. every</div>
                <div className="v">{avgEvery ?? '—'}<small> days</small></div>
              </div>
              <div className="kpi">
                <div className="t">Last visit</div>
                <div className="v">{lastVisitDays ?? '—'}<small> {lastVisitDays === 1 ? 'day' : 'days'} ago</small></div>
              </div>
            </div>

            <div className="panel" data-testid="visit-history">
              <div className="ph">
                <h3>Visit history</h3>
                <span style={{ fontSize: 12, color: 'var(--mute)' }}>Most recent first</span>
              </div>
              {!features.visitHistory ? (
                <div className="upgrade-nudge" style={{ margin: 16, borderRadius: 9 }} data-testid="visit-history-locked">
                  <span><b>Visit history</b> is a Premium feature. Upgrade to see every past appointment.</span>
                  <Link to="/billing" className="btn p">Upgrade →</Link>
                </div>
              ) : bookings.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--mute)', fontSize: 13 }}>
                  No bookings yet.
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Service</th>
                      <th>Barber</th>
                      <th>Deposit</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map(b => {
                      const pill = STATUS_PILL[b.status] || STATUS_PILL.confirmed;
                      return (
                        <tr key={b.id}>
                          <td className="mono">{fmtDate(b.starts_at)}</td>
                          <td>{b.service?.name || '—'}</td>
                          <td>{b.barber?.name || '—'}</td>
                          <td className="mono">{b.deposit_paid ? fmtMoney(b.deposit_amount) : '—'}</td>
                          <td><span className={`pill ${pill.cls}`}>{pill.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
};

const ClientDetail = () => (
  <AppShell>
    <ClientDetailInner />
  </AppShell>
);

export default ClientDetail;
